import {SET_DASHBOARD, SET_DASHBOARD_LOADING} from "../reducers/dashboard";
import {makeRequest} from "../util";
import {WarehouseDashboardResponse, WarehouseStatus} from "../DashboardPage/types";
import {LoadedDestinationInfo, LoadedEventDefintion} from "../types/global";
import {destinationKeyToSlugMap, WarehouseDestination, warehouseDestinations} from "../destinations/destinations";
import {WarehouseDestinationSlug} from "../destinations/types";
import _ from "lodash";
import {Dispatch} from "redux";

type DashboardBuildHelperFnParams = {
    eventDefs: LoadedEventDefintion[],
    destinationResponse: LoadedDestinationInfo[],
    dashboardResponse: WarehouseDashboardResponse
};

type DashboardBuildHelperFn<T> = (params: DashboardBuildHelperFnParams) => T;

/**
 * Builds a mapping of destination id => (mapping of eventId => boolean), where the boolean represents if the event is enabled for that destination.
 * @param eventDefs
 * @param destinationResponse
 * @param dashboardResponse
 */
const buildEventsPerDestinationMap: DashboardBuildHelperFn<Record<string, Record<string, boolean>>> = ({
      eventDefs,
      destinationResponse,
      dashboardResponse,
}) => {
    const eventsPerDestination: Record<string, Record<string, boolean>> = {};

    dashboardResponse.forEach((warehouseResponse) => {
        const {destination_id} = warehouseResponse;
        const foundDestination = destinationResponse.find(
            (destInfo: LoadedDestinationInfo) => destInfo.id === destination_id
        );

        if (foundDestination) {
            const enabledEvents: Record<string, boolean> = {};

            eventDefs.forEach((eventDef: LoadedEventDefintion) => {
                enabledEvents[eventDef.id] = !!Object.keys(eventDef.enabled_dests).find((destID) => destID === destination_id);
            });

            eventsPerDestination[destination_id] = enabledEvents;
        }
    })

    return eventsPerDestination;
}

const buildDashboardData: DashboardBuildHelperFn<WarehouseStatus[]> = ({
    eventDefs,
    destinationResponse,
    dashboardResponse,
}) => {
    const eventsPerDestination = buildEventsPerDestinationMap({eventDefs, destinationResponse, dashboardResponse});

    return dashboardResponse.map(
        (warehouseResponse) => {
            const {destination_id} = warehouseResponse;
            const foundDestination = destinationResponse.find(
                (destInfo: LoadedDestinationInfo) => destInfo.id === destination_id
            );

            if (foundDestination) {
                const destinationSlug = destinationKeyToSlugMap[foundDestination.name] as WarehouseDestinationSlug;

                if (destinationSlug) {
                    const destinationData: WarehouseDestination | undefined = warehouseDestinations[destinationSlug];

                    return {
                        destination: foundDestination,
                        warehouse_destination: destinationData,
                        tables: warehouseResponse.tables,
                    };
                }
            }
        })
        .filter(_.identity)
        .sort((a, b) => {
            if (a && b) {
                return a.destination.id > b.destination.id ? -1 : 1
            } else {
                return 0
            }
        }) as WarehouseStatus[];
}

export const fetchDashboardData = () => {
    return async (dispatch: Dispatch) => {
        dispatch({type: SET_DASHBOARD_LOADING});

        // Data we need for the dashboard consists of the `warehouse_status` request combined with data from the event defs.
        const {data: eventDefData} = await makeRequest<{ event_defs: LoadedEventDefintion[], dest_info: LoadedDestinationInfo[] }>("get_event_defs", {});
        const {data: dashboardResponse} = await makeRequest<WarehouseDashboardResponse>("warehouse_status", {});

        const {dest_info: destinationResponse, event_defs: eventDefs} = eventDefData;
        const dashboardData: WarehouseStatus[] = buildDashboardData({
            eventDefs,
            destinationResponse,
            dashboardResponse
        });

        dispatch({type: SET_DASHBOARD, payload: dashboardData});
    };
};
